import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Coins, Plus, ChevronDown, ArrowUpRight, AlertTriangle, Gift,
  ShieldCheck, Trophy,
} from "lucide-react";
import api from "../lib/api";
import CountUp from "./CountUp";

/**
 * DiploksLiveBalanceWidget
 * ----------------------------------------------------------------------
 * Top-right header pill that surfaces the current user's *active* wallet
 * balance + a dropdown to switch between every wallet they can act on:
 *
 *   - Personal Diploks wallet (every eligible role, auto-created)
 *   - Organisation Diploks wallet (org_admin / event_manager / school_admin / super_admin)
 *   - Linked child wallets (parents — Sprint R3 enables top-up)
 *
 * The widget is intentionally compact, self-hides for policy-restricted
 * roles (sponsor / partner_institution / observer) and refreshes after
 * Diploks events via the global `diploks:wallet-changed` window event
 * other components dispatch after a top-up/consume/grant/refund.
 *
 * Backend source of truth: GET /api/diploks/live-balance.
 */
export default function DiploksLiveBalanceWidget() {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/diploks/live-balance");
      if (!r.data.eligible) {
        setHidden(true);
        return;
      }
      setHidden(false);
      setData(r.data);
    } catch {
      setHidden(true);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    const tick = () => { if (alive) refresh(); };
    tick();
    const onChange = () => tick();
    window.addEventListener("diploks:wallet-changed", onChange);
    window.addEventListener("focus", onChange);
    // Soft poll every 60s — backend is the source of truth, this only
    // catches grants/refunds that happen outside the current tab.
    const t = setInterval(tick, 60_000);
    return () => {
      alive = false;
      window.removeEventListener("diploks:wallet-changed", onChange);
      window.removeEventListener("focus", onChange);
      clearInterval(t);
    };
  }, [refresh]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (hidden || !data) return null;
  const active = data.active;
  if (!active) return null;
  const isLow = active.low_balance;
  const isOrg = active.scope === "organisation";

  return (
    <div
      ref={ref}
      className="relative"
      data-testid="diploks-live-balance-widget"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="diploks-live-balance-trigger"
        data-scope={active.scope}
        className={`inline-flex items-center gap-2 pl-2.5 pr-2 py-1.5 rounded-full border transition-all
          ${isLow
            ? "bg-amber-50 border-amber-200 text-amber-900 hover:border-amber-400"
            : "bg-[#0A192F] text-white border-[#D4AF37]/40 hover:border-[#D4AF37]"
          }`}
        title={`${active.label}: ${active.balance.toLocaleString()} Diploks available`}
      >
        <Coins
          className={`h-3.5 w-3.5 ${isLow ? "text-amber-700" : "text-[#D4AF37]"}`}
        />
        <span
          className="text-xs font-bold tabular-nums"
          data-testid="diploks-live-balance-value"
        >
          <CountUp
            to={active.balance}
            duration={0.8}
            milestoneEvery={100}
            milestoneMin={100}
          />
        </span>
        <span className="hidden sm:inline text-[10px] uppercase tracking-wider opacity-80">
          Diploks
        </span>
        {isLow && (
          <span
            data-testid="diploks-live-balance-low-pill"
            className="hidden sm:inline text-[9px] uppercase font-bold bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded"
          >
            Low
          </span>
        )}
        <ChevronDown
          className={`h-3 w-3 opacity-70 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          data-testid="diploks-live-balance-dropdown"
          className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-slate-200 shadow-xl z-50 overflow-hidden"
        >
          <div className="px-4 py-3 bg-gradient-to-r from-[#0A192F] to-[#0F2C50] text-white">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
              {active.label}
            </p>
            <div className="mt-1 flex items-end gap-2">
              <p
                className="text-2xl font-bold tabular-nums leading-none"
                data-testid="diploks-live-balance-active-amount"
              >
                {active.balance.toLocaleString()}
              </p>
              <p className="text-xs text-slate-300 mb-0.5">Diploks available</p>
            </div>
            {isLow && (
              <p className="mt-2 text-[11px] text-amber-200 inline-flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> Top up to keep premium
                features running.
              </p>
            )}
          </div>

          <div className="p-3 space-y-2">
            <Link
              to={isOrg ? "/dashboard/diploks/topup" : "/dashboard/diploks/personal/topup"}
              data-testid="diploks-live-balance-topup-cta"
              onClick={() => setOpen(false)}
              className="w-full flex items-center justify-center gap-1.5 bg-[#D4AF37] text-[#0A192F] font-bold text-[11px] uppercase tracking-wider px-3 py-2 rounded-md hover:bg-[#B5952F] transition-colors"
            >
              <Plus className="h-3.5 w-3.5" /> Top up Diploks
            </Link>
            <Link
              to={active.deep_link || "/dashboard/diploks/personal/wallet"}
              onClick={() => setOpen(false)}
              data-testid="diploks-live-balance-open-wallet"
              className="w-full flex items-center justify-center gap-1.5 text-[11px] text-[#0A192F] font-semibold py-1.5 hover:underline"
            >
              View wallet & ledger <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>

          {data.wallets.length > 1 && (
            <div className="border-t border-slate-100">
              <p className="px-4 pt-3 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
                Switch wallet
              </p>
              <div className="px-2 py-2 space-y-1">
                {data.wallets.map((w) => (
                  <Link
                    key={w.wallet_id}
                    to={w.deep_link}
                    onClick={() => setOpen(false)}
                    data-testid={`diploks-wallet-switcher-${w.scope}`}
                    className={`flex items-center justify-between px-3 py-2 rounded-md text-xs transition-colors ${
                      w.wallet_id === active.wallet_id
                        ? "bg-[#D4AF37]/15 border border-[#D4AF37]/40"
                        : "hover:bg-slate-50"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {w.scope === "organisation" ? (
                        <ShieldCheck className="h-3.5 w-3.5 text-[#0F4C81]" />
                      ) : w.scope === "parent_child" ? (
                        <Gift className="h-3.5 w-3.5 text-[#0F4C81]" />
                      ) : (
                        <Coins className="h-3.5 w-3.5 text-[#0A192F]" />
                      )}
                      <span className="font-semibold text-[#0A192F]">{w.label}</span>
                    </span>
                    <span className="font-bold tabular-nums">
                      {w.balance.toLocaleString()}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-slate-100 px-4 py-3 bg-slate-50">
            <p className="text-[10px] text-slate-500 leading-relaxed">
              {data.compliance_notice ||
                "Diploks are Diplomexa platform credits — not cash, not crypto, not withdrawable."}
            </p>
            {data.podium_note && (
              <p className="mt-1.5 text-[10px] text-slate-500 inline-flex items-center gap-1">
                <Trophy className="h-3 w-3 text-[#D4AF37]" /> {data.podium_note}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
